import React, { useEffect, useState } from 'react';
import {
  Box, Stack, TextField, Table, TableContainer, TableHead, TableRow, TableCell, TableBody,
  Typography, Switch, Tooltip, ToggleButtonGroup, ToggleButton,
  Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { LoadingOverlay, EmptyState, ConfirmDialog, GlassIconButton, BrandLogo } from 'dashboard/components/common';
import AdminTableFooter from './AdminTableFooter';
import SortableHeader from './SortableHeader';
import BrandDetailsDrawer from './BrandDetailsDrawer';

const PAGE_SIZE = 50;

const isBrandActive = (b) => b?.is_active !== false && b?.active !== false;

export default function BrandsTab() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ sort_by: null, sort_dir: null });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [detailsId, setDetailsId] = useState(null);

  const onExport = () => {
    setExporting(true);
    API.adminExportBrands({
      search,
      status: statusFilter === 'all' ? undefined : statusFilter,
      sort_by: sort.sort_by || undefined,
      sort_dir: sort.sort_dir || undefined,
    })
      .catch((err) => toast.error(err?.message || 'Failed to export brands'))
      .finally(() => setExporting(false));
  };

  const applyToggle = (brand, nextActive) => {
    setBusyId(brand._id);
    API.adminSetBrandActive({ _id: brand._id, is_active: nextActive })
      .then(() => {
        setRows((cur) => cur.map((b) => (b._id === brand._id ? { ...b, is_active: nextActive, active: nextActive } : b)));
        toast.success(nextActive ? 'Brand reactivated' : 'Brand disabled');
      })
      .catch((err) => toast.error(err?.message || 'Failed to update brand'))
      .finally(() => { setBusyId(null); setConfirm(null); });
  };

  useEffect(() => { setPage(1); }, [search, statusFilter, sort.sort_by, sort.sort_dir]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      API.adminListBrands({
        search,
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: PAGE_SIZE,
        skip: (page - 1) * PAGE_SIZE,
        sort_by: sort.sort_by || undefined,
        sort_dir: sort.sort_dir || undefined,
      })
        .then((res) => {
          if (cancelled) return;
          setRows(res?.items || []);
          setTotal(res?.total || 0);
        })
        .catch((err) => toast.error(err?.message || 'Failed to load brands', { toastId: 'admin-brands' }))
        .finally(() => !cancelled && setLoading(false));
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, statusFilter, page, sort.sort_by, sort.sort_dir]);

  return (
    <Box>
      <Stack direction='row' alignItems='center' mb='12px' spacing='12px' flexWrap='wrap'>
        <TextField
          size='small'
          placeholder='Search by brand name, domain, slug…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 320 }}
        />
        <ToggleButtonGroup
          size='small'
          exclusive
          value={statusFilter}
          onChange={(_e, v) => v && setStatusFilter(v)}
        >
          <ToggleButton value='all'>All</ToggleButton>
          <ToggleButton value='active'>Active</ToggleButton>
          <ToggleButton value='disabled'>Disabled</ToggleButton>
        </ToggleButtonGroup>
        <Box flex={1} />
        <GlassIconButton
          size='small'
          label='Download CSV of the current view'
          disabled={exporting}
          disabledReason={exporting ? 'Export already running — the file will download when it is ready.' : ''}
          onClick={onExport}
        >
          <DownloadIcon fontSize='small' />
        </GlassIconButton>
      </Stack>

      <LoadingOverlay loading={loading} minHeight={240}>
        {rows.length === 0 && !loading ? (
          <EmptyState>No brands match your search.</EmptyState>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <SortableHeader field='name'      sortBy={sort.sort_by} sortDir={sort.sort_dir} onSort={setSort}>Brand</SortableHeader>
                <SortableHeader field='domain'    sortBy={sort.sort_by} sortDir={sort.sort_dir} onSort={setSort}>Domain</SortableHeader>
                <TableCell>Owner</TableCell>
                <TableCell>Active</TableCell>
                <SortableHeader field='createdAt' sortBy={sort.sort_by} sortDir={sort.sort_dir} onSort={setSort}>Created</SortableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((b) => (
                <TableRow
                  key={b._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setDetailsId(b._id)}
                >
                  <TableCell>
                    <Stack direction='row' alignItems='center' spacing='8px'>
                      <BrandLogo
                        src={b.logo_upload_path}
                        name={b.name}
                        size={28}
                        sx={{ opacity: isBrandActive(b) ? 1 : 0.4 }}
                      />
                      <Box>
                        <Stack direction='row' spacing='6px' alignItems='center'>
                          <Typography
                            fontWeight='600'
                            sx={{ textDecoration: isBrandActive(b) ? 'none' : 'line-through' }}
                          >
                            {b.name}
                          </Typography>
                          {!isBrandActive(b) && (
                            <Chip size='small' color='error' label='Disabled' />
                          )}
                        </Stack>
                        {b.slug && <Typography variant='caption' color='text.secondary'>{b.slug}</Typography>}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>{b.domain || '—'}</TableCell>
                  <TableCell>
                    {b.owner ? (
                      <Box>
                        <Typography variant='body2'>{b.owner.email}</Typography>
                        <Typography variant='caption' color='text.secondary'>{b.owner.username}</Typography>
                      </Box>
                    ) : '—'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={isBrandActive(b) ? 'Disable brand' : 'Reactivate brand'}>
                      <span>
                        <Switch
                          size='small'
                          checked={isBrandActive(b)}
                          disabled={busyId === b._id}
                          onChange={(e) => {
                            const next = e.target.checked;
                            if (next) applyToggle(b, true);
                            else setConfirm({ brand: b, next });
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{b.createdAt ? new Date(b.createdAt).toLocaleDateString() : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            </Table>
          </TableContainer>
        )}
      </LoadingOverlay>
      <AdminTableFooter
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onChange={setPage}
        label='brands'
      />
      <ConfirmDialog
        open={!!confirm}
        title='Disable brand?'
        message={confirm ? `${confirm.brand.name} won't accept dashboard or storefront requests until you re-enable it. Existing customer sessions stay until they expire.` : ''}
        confirmLabel='Disable'
        confirmColor='error'
        busy={!!busyId}
        onConfirm={() => confirm && applyToggle(confirm.brand, confirm.next)}
        onCancel={() => setConfirm(null)}
      />
      <BrandDetailsDrawer
        brandId={detailsId}
        onClose={() => setDetailsId(null)}
      />
    </Box>
  );
}
